/**
 * NHS / RCPCH developmental milestones.
 * Based on: NHS Personal Child Health Record (Red Book),
 * RCPCH "Health for All Children" (Hall & Elliman), and NHS developmental review guidance.
 * For informational use only — always consult your health visitor or GP.
 */

export const AGE_BRACKETS = [2, 4, 6, 9, 12, 15, 18, 24];

export const NHS_MILESTONES = {
  2: {
    label: "2 Months",
    domains: [
      {
        key: "social", label: "Social & Emotional", icon: "💛",
        items: [
          "Social smile — smiles back when you smile at them",
          "Settles when picked up and spoken to",
          "Watches your face intently",
          "Shows excitement when they see you approaching",
        ],
      },
      {
        key: "language", label: "Language & Communication", icon: "💬",
        items: [
          "Makes small cooing or throaty noises",
          "Reacts to loud or sudden sounds",
        ],
      },
      {
        key: "cognitive", label: "Cognitive", icon: "🧠",
        items: [
          "Follows a face or slow-moving object through a 90° arc",
          "Briefly focuses on a brightly coloured toy held nearby",
        ],
      },
      {
        key: "motor", label: "Movement & Physical", icon: "💪",
        items: [
          "Lifts head momentarily when placed on their tummy",
          "Moves arms and legs equally on both sides",
          "Hands loosely open or closed",
        ],
      },
    ],
  },

  4: {
    label: "4 Months",
    domains: [
      {
        key: "social", label: "Social & Emotional", icon: "💛",
        items: [
          "Laughs and chuckles during interaction",
          "Recognises familiar faces and voices",
          "Shows pleasure and displeasure clearly",
          "Reaches out toward familiar people",
        ],
      },
      {
        key: "language", label: "Language & Communication", icon: "💬",
        items: [
          "Coos and vocalises in response to your voice",
          "Squeals and gurgles with excitement",
          "Turns head toward the sound of your voice",
        ],
      },
      {
        key: "cognitive", label: "Cognitive", icon: "🧠",
        items: [
          "Reaches for objects placed in front of them",
          "Follows a moving toy with their eyes",
          "Looks at own hands with interest",
        ],
      },
      {
        key: "motor", label: "Movement & Physical", icon: "💪",
        items: [
          "Holds head steady when held upright",
          "Brings hands together at the midline",
          "Rolls from front to side",
          "Puts hands to mouth",
        ],
      },
    ],
  },

  6: {
    label: "6 Months",
    domains: [
      {
        key: "social", label: "Social & Emotional", icon: "💛",
        items: [
          "Distinguishes strangers from familiar people",
          "Smiles at own reflection in a mirror",
          "Enjoys playing with you — shows displeasure when play stops",
        ],
      },
      {
        key: "language", label: "Language & Communication", icon: "💬",
        items: [
          "Babbles with single syllables (e.g. 'ba', 'da', 'ga')",
          "Turns head to a soft sound out of sight",
          "Begins to respond to tone of voice",
        ],
      },
      {
        key: "cognitive", label: "Cognitive", icon: "🧠",
        items: [
          "Reaches for objects with one hand",
          "Passes objects from hand to hand",
          "Explores objects by mouthing them",
        ],
      },
      {
        key: "motor", label: "Movement & Physical", icon: "💪",
        items: [
          "Sits with support, or briefly without support",
          "Rolls from back to front",
          "Bears weight on legs when held standing",
          "Uses a raking grasp to pick up objects",
        ],
      },
    ],
  },

  9: {
    label: "9 Months",
    domains: [
      {
        key: "social", label: "Social & Emotional", icon: "💛",
        items: [
          "Wary or upset around strangers (stranger anxiety)",
          "Becomes distressed when you leave the room",
          "Enjoys social games like peekaboo and 'round and round the garden'",
          "Looks to you to gauge reactions in new situations",
        ],
      },
      {
        key: "language", label: "Language & Communication", icon: "💬",
        items: [
          "Babbles with two syllables (e.g. 'mama', 'dada') without meaning",
          "Shouts and vocalises to attract attention",
          "Understands 'no' and responds to their own name",
          "Holds arms up to be lifted",
        ],
      },
      {
        key: "cognitive", label: "Cognitive", icon: "🧠",
        items: [
          "Looks for a dropped or hidden object (object permanence)",
          "Imitates simple actions such as banging or clapping",
          "Explores objects by turning, shaking, and examining them",
        ],
      },
      {
        key: "motor", label: "Movement & Physical", icon: "💪",
        items: [
          "Sits independently for several minutes",
          "Moves around by crawling, bottom-shuffling, or rolling",
          "Pulls themselves to standing",
          "Points with or uses index finger to poke and explore",
        ],
      },
    ],
  },

  12: {
    label: "12 Months",
    domains: [
      {
        key: "social", label: "Social & Emotional", icon: "💛",
        items: [
          "Waves goodbye",
          "Shows affection to familiar people",
          "Holds out objects to show you (but may not let go)",
          "Plays simple social games (pat-a-cake, peekaboo)",
        ],
      },
      {
        key: "language", label: "Language & Communication", icon: "💬",
        items: [
          "Says 1–2 words with meaning (e.g. 'mama', 'dada', 'no', 'bye')",
          "Understands and responds to simple one-step instructions",
          "Points with index finger at things they want or find interesting",
        ],
      },
      {
        key: "cognitive", label: "Cognitive", icon: "🧠",
        items: [
          "Points to share interest in something, not just to request",
          "Imitates everyday actions (brushing hair, talking on phone)",
          "Uses objects correctly (raises cup to mouth to drink)",
        ],
      },
      {
        key: "motor", label: "Movement & Physical", icon: "💪",
        items: [
          "Cruises around furniture or walks with one hand held",
          "May stand briefly without support",
          "Has developed a neat pincer grip (thumb and forefinger)",
          "Deliberately drops objects",
        ],
      },
    ],
  },

  15: {
    label: "15 Months",
    domains: [
      {
        key: "social", label: "Social & Emotional", icon: "💛",
        items: [
          "Shows affection with hugs and kisses to familiar people",
          "Points to share interest — not just to get something",
          "Begins to show independence ('I do it!')",
          "May show jealousy or signs of frustration",
        ],
      },
      {
        key: "language", label: "Language & Communication", icon: "💬",
        items: [
          "Uses 6–10 recognisable words",
          "Shakes head for 'no'",
          "Follows simple instructions without gestures",
          "Refers to themselves by name",
        ],
      },
      {
        key: "cognitive", label: "Cognitive", icon: "🧠",
        items: [
          "Scribbles with a crayon",
          "Points to familiar body parts when asked",
          "Begins simple pretend play (pretending to drink from an empty cup)",
          "Explores cause and effect with toys",
        ],
      },
      {
        key: "motor", label: "Movement & Physical", icon: "💪",
        items: [
          "Walks unaided",
          "Climbs onto low furniture",
          "Builds a tower of 2 bricks",
          "Uses a spoon (messily)",
        ],
      },
    ],
  },

  18: {
    label: "18 Months",
    domains: [
      {
        key: "social", label: "Social & Emotional", icon: "💛",
        items: [
          "Shows empathy — becomes concerned when others are upset",
          "Plays alongside other children (parallel play)",
          "Enjoys listening to simple stories and nursery rhymes",
          "Helps with simple household tasks (fetching, putting things away)",
          "Shows a range of emotions (affection, frustration, fear)",
        ],
      },
      {
        key: "language", label: "Language & Communication", icon: "💬",
        items: [
          "Uses 10–20 or more recognisable words",
          "Begins combining two words (e.g. 'more milk', 'daddy go')",
          "Names familiar people and objects in pictures",
          "Follows two-step instructions (e.g. 'get your shoes and bring them here')",
        ],
      },
      {
        key: "cognitive", label: "Cognitive", icon: "🧠",
        items: [
          "Engages in pretend play (feeding a doll, talking on a toy phone)",
          "Sorts objects roughly by shape or colour",
          "Understands simple concepts such as 'big' and 'little'",
        ],
      },
      {
        key: "motor", label: "Movement & Physical", icon: "💪",
        items: [
          "Runs, though may fall often",
          "Walks upstairs with hand held",
          "Kicks a ball",
          "Builds a tower of 3 or more bricks",
          "Uses a spoon without too much spilling",
        ],
      },
    ],
  },

  24: {
    label: "2 Years",
    domains: [
      {
        key: "social", label: "Social & Emotional", icon: "💛",
        items: [
          "Plays alongside and beginning to play with other children",
          "Understands 'mine' and 'yours' — developing sense of ownership",
          "Shows concern and sympathy for others",
          "May start to show defiant or challenging behaviour (normal at this age)",
        ],
      },
      {
        key: "language", label: "Language & Communication", icon: "💬",
        items: [
          "Uses 50 or more recognisable words",
          "Puts 2–3 words together regularly (e.g. 'where daddy gone?')",
          "Asks simple questions ('What's that?', 'Where's teddy?')",
          "Speech understood by familiar adults most of the time",
          "Refers to themselves by name or begins to use 'I' or 'me'",
        ],
      },
      {
        key: "cognitive", label: "Cognitive", icon: "🧠",
        items: [
          "Engages in imaginative and symbolic pretend play",
          "Matches shapes in a simple puzzle",
          "Follows two-step instructions reliably",
          "Understands the concept of 'one'",
        ],
      },
      {
        key: "motor", label: "Movement & Physical", icon: "💪",
        items: [
          "Runs with confidence and changes direction",
          "Walks up and down stairs (holding on)",
          "Kicks and throws a ball",
          "Builds a tower of 6 or more bricks",
          "Uses a spoon and fork",
          "Makes circular scribbles",
        ],
      },
    ],
  },
};

/** Returns the current and all past age brackets for a child of ageMonths */
export const getBracketsForAge = (ageMonths) => {
  if (ageMonths == null) return [];
  return AGE_BRACKETS.filter((b) => b <= ageMonths + 1); // +1 so bracket just passed still shows
};

/** Returns the most relevant bracket for the child's current age */
export const getCurrentBracket = (ageMonths) => {
  if (ageMonths == null) return null;
  const past = AGE_BRACKETS.filter((b) => b <= ageMonths);
  return past.length > 0 ? past[past.length - 1] : AGE_BRACKETS[0];
};
